import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./IroncladPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Ironclad" — a NON-ONBOARDING, in-app upgrade prompt built as a PRIVACY
// CHECKUP. An existing free user opens it from Vault settings; the screen runs
// a live audit of how their photos are protected and reports back.
//
// Behavioural spine: PROGRESS COMPLETION + GOAL GRADIENT, kept scrupulously
// honest. The foundation protections that free already gives are shown ON and
// celebrated (no fear, no "you are exposed"). Premium is framed as CLOSING THE
// LAST GAPS to finish an already-started job — 3 of 7 layers on → 7 of 7,
// "Ironclad." People finish what looks nearly finished; this makes finishing
// concrete and satisfying rather than frightening.
//
// Nothing is reused from another paywall. The layout is an audit console — a
// single segmented PROTECTION STACK that is simultaneously the meter and the
// feature list, with a left coverage rail that fills as layers seat — not a
// hero image + bullet list + price card. The mechanic (a top-down scan that
// registers each layer, then seats the open gaps closed with a forged-steel
// IRONCLAD stamp) is new. The register is a calm expert checkup, not the streak
// pride, ring-warmth or unlock drama of the security paywalls before it. Only
// the house fonts, colour tokens and existing feature icons are reused.
//
// Yearly is the hero: largest price, per-month broken out, honest Save %, the
// default selection, the 7-day trial. Monthly is a small secondary line. Copy
// is App-Store compliant: the basics stay on whatever happens, photos are never
// said to be lost, there is no countdown or scarcity, and the savings claim is
// real (yearly vs. 12× monthly).
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const thumb = (n: number) => `${PUB}/ironclad/m${n}.jpg`

// The vault the checkup is protecting. Debug data ships zeros, so these anchor
// the concept on plain renders (the native app would inject the real figures).
const NUM_PHOTOS = 1248
const NUM_ALBUMS = 36
const CORE_THUMBS = [1, 2, 3, 4, 5, 6]

type Layer = {
  key: string
  Icon: ComponentType<{ className?: string }>
  label: string
  note: string
  premium: boolean
}

// Seven real protection layers, ordered foundation → advanced. The first three
// ship with the free vault and are shown ON. The last four are the real Premium
// features and are the "gaps" the checkup surfaces. Rendered top-first, so the
// open gaps sit above the solid foundation and visibly seat down onto it.
const LAYERS: Layer[] = [
  {
    key: "passcode",
    Icon: svg.featureIcons.PatternLock,
    label: "Passcode lock",
    note: "Your vault opens only with your code",
    premium: false,
  },
  {
    key: "albums",
    Icon: svg.featureIcons.OrganizationPlus,
    label: "Private albums",
    note: "Sorted, named, kept out of sight",
    premium: false,
  },
  {
    key: "hidden",
    Icon: svg.featureIcons.Disguise,
    label: "Hidden from Photos",
    note: "Never shows in your camera roll",
    premium: false,
  },
  {
    key: "unlock",
    Icon: svg.featureIcons.SecurityPlus,
    label: "Only-You Unlock",
    note: "Face ID / Touch ID — no code to leak",
    premium: true,
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    label: "Encrypted cloud backup",
    note: "A safe copy, sealed end-to-end",
    premium: true,
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    label: "Break-in report",
    note: "A photo of anyone who tries a wrong code",
    premium: true,
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    label: "Decoy vault",
    note: "A second code opens a harmless album",
    premium: true,
  },
]

const TOTAL = LAYERS.length // 7
const BASE = LAYERS.filter((l) => !l.premium).length // 3 foundation layers on now

// Named protection levels the coverage rail climbs through. Honest and
// qualitative — the number shown is a concrete "X of 7 layers on", never a
// made-up safety percentage.
function levelName(lit: number): string {
  if (lit >= TOTAL) return "Ironclad"
  if (lit >= 5) return "Strong"
  if (lit >= BASE) return "Standard"
  return "Checking…"
}

function IroncladPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""

  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // `lit` = how many layers are sealed, counted from the foundation up. It is
  // the single source of truth for the rail height, the readout and which bars
  // read as On. Starts at 0 and the load scan registers the foundation up to
  // BASE; a teaching preview later seals all TOTAL then recedes.
  const [lit, setLit] = useState(reduce ? BASE : 0)
  const [sealed, setSealed] = useState(false) // committed (CTA pressed)
  const [scan, setScan] = useState(false) // scan line sweeping
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)

  const litAnim = useRef<number | undefined>(undefined)
  const timers = useRef<number[]>([])

  const animateLitTo = useCallback(
    (target: number, stepMs: number, done?: () => void) => {
      if (litAnim.current) window.clearInterval(litAnim.current)
      if (reduce) {
        setLit(target)
        done?.()
        return
      }
      litAnim.current = window.setInterval(() => {
        setLit((cur) => {
          if (cur === target) {
            if (litAnim.current) window.clearInterval(litAnim.current)
            done?.()
            return cur
          }
          return cur + (cur < target ? 1 : -1)
        })
      }, stepMs)
    },
    [reduce],
  )

  // On load: sweep a scan line, register the foundation layers one by one, then
  // run a single teaching preview that seals every gap (with the stamp) and
  // recedes — the reader sees the destination, then the honest current state.
  useEffect(() => {
    const push = (fn: () => void, ms: number) =>
      timers.current.push(window.setTimeout(fn, ms))

    push(() => setShowClose(true), reduce ? 0 : 1400)

    if (reduce) {
      setLit(BASE)
      return () => timers.current.forEach(window.clearTimeout)
    }

    setScan(true)
    push(() => setScan(false), 1150)
    // register foundation layers as the scan passes them
    push(() => animateLitTo(BASE, 220), 260)
    // teaching preview: seal everything, hold, then recede to the honest state
    push(() => animateLitTo(TOTAL, 90), 1300)
    push(() => animateLitTo(BASE, 150), 2500)

    return () => {
      timers.current.forEach(window.clearTimeout)
      if (litAnim.current) window.clearInterval(litAnim.current)
    }
  }, [reduce, animateLitTo])

  // Tapping a still-open gap previews sealing the whole stack — a light,
  // exploratory "show me what Premium does" beat.
  const previewSeal = useCallback(() => {
    if (sealed) return
    animateLitTo(TOTAL, 70, () => {
      timers.current.push(
        window.setTimeout(() => {
          if (!sealed) animateLitTo(BASE, 150)
        }, 1100),
      )
    })
  }, [animateLitTo, sealed])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setSealed(true)
    animateLitTo(TOTAL, 60, () => {
      timers.current.push(
        window.setTimeout(() => {
          if (product) native.subscribe(product)
        }, 620),
      )
    })
  }, [plan, yearly, monthly, animateLitTo])

  const handleClose = () => native.postNativeEvent("close")

  const isIronclad = lit >= TOTAL
  const railPct = Math.round((lit / TOTAL) * 100)
  const level = levelName(lit)

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after a 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${isIronclad ? styles.rootSealed : ""}`}
      data-sealed={sealed ? "true" : "false"}
    >
      <div className={styles.wash} aria-hidden>
        <span className={styles.glowSteel} />
        <span className={styles.glowSecured} />
        <span className={styles.grid} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 8, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            <span className={styles.scanDot} />
            Privacy checkup
          </p>
          <h1 className={styles.title}>
            Make your vault
            <br />
            <span className={styles.titleForged}>ironclad.</span>
          </h1>
          <p className={styles.sub}>
            We checked how your photos are protected. The basics are on —
            Premium closes the last {TOTAL - BASE} gaps, so nothing about your
            memories is left to chance.
          </p>
        </header>

        {/* ── The audit console: rail + protection stack ─────────────────── */}
        <section className={styles.console} aria-label="Protection checkup">
          {/* Readout */}
          <div className={styles.readout}>
            <div className={styles.readLeft}>
              <span className={styles.readLabel}>Protection level</span>
              <span
                className={`${styles.readLevel} ${
                  isIronclad ? styles.readLevelMax : ""
                }`}
              >
                {level}
                {isIronclad && (
                  <span className={styles.stamp} aria-hidden>
                    <svg viewBox="0 0 24 24" width="13" height="13">
                      <path
                        d="M5 12.5l4.4 4.4L19 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    IRONCLAD
                  </span>
                )}
              </span>
            </div>
            <div className={styles.readRight}>
              <span className={styles.readCount}>
                <b>{lit}</b>
                <span>/ {TOTAL}</span>
              </span>
              <span className={styles.readCountLabel}>layers on</span>
            </div>
          </div>

          <div className={styles.stackWrap}>
            {/* coverage rail */}
            <div className={styles.rail} aria-hidden>
              <span
                className={styles.railFill}
                style={{ height: `${railPct}%` }}
              />
              {LAYERS.map((_, i) => (
                <span key={i} className={styles.railTick} />
              ))}
            </div>

            {/* the protection stack — top = open gaps, bottom = foundation */}
            <ol className={`${styles.stack} ${scan ? styles.stackScan : ""}`}>
              {LAYERS.map((_l, revI) => {
                // Render top-first (open gaps on top) but keep a foundation-up
                // logical index so `lit` fills the foundation before the gaps.
                const idx = TOTAL - 1 - revI
                const layer = LAYERS[idx]
                const on = idx < lit
                return (
                  <li
                    key={layer.key}
                    className={[
                      styles.layer,
                      layer.premium ? styles.layerPremium : styles.layerBase,
                      on ? styles.layerOn : styles.layerOff,
                    ].join(" ")}
                    style={{ ["--seat" as any]: `${(TOTAL - idx) * 45}ms` }}
                  >
                    <button
                      type="button"
                      className={styles.layerHit}
                      onClick={layer.premium ? previewSeal : undefined}
                      aria-label={`${layer.label} — ${
                        on ? "on" : "available with Premium"
                      }`}
                    >
                      <span className={styles.layerIconWrap}>
                        <layer.Icon className={styles.layerIcon} />
                      </span>
                      <span className={styles.layerText}>
                        <span className={styles.layerLabel}>
                          {layer.label}
                          {layer.premium && (
                            <span className={styles.premiumTag}>Premium</span>
                          )}
                        </span>
                        <span className={styles.layerNote}>{layer.note}</span>
                      </span>
                      <span className={styles.layerState} aria-hidden>
                        <span className={styles.stateOn}>
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              d="M5 12.5l4.4 4.4L19 7"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span className={styles.stateOff}>
                          <svg viewBox="0 0 24 24" width="15" height="15">
                            <path
                              d="M8 10V7.5a4 4 0 0 1 7.5-1.9"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <rect
                              x="5.5"
                              y="10"
                              width="13"
                              height="9"
                              rx="2.3"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ol>
          </div>

          {/* what is being protected — brand clarity */}
          <div className={styles.core}>
            <div className={styles.coreThumbs} aria-hidden>
              {CORE_THUMBS.map((n) => (
                <span key={n} className={styles.coreThumb}>
                  <img
                    src={thumb(n)}
                    alt=""
                    draggable={false}
                    loading="eager"
                  />
                </span>
              ))}
              <span className={styles.coreLock} aria-hidden>
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M8 10V7a4 4 0 0 1 8 0v3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5.5"
                    y="10"
                    width="13"
                    height="9.5"
                    rx="2.4"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </div>
            <p className={styles.coreLabel}>
              Guarding <b>{NUM_PHOTOS.toLocaleString()}</b> photos ·{" "}
              <b>{NUM_ALBUMS}</b> albums
            </p>
          </div>
        </section>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · Save {savings}%
              </span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Yearly</span>
                <span className={styles.planCovers}>
                  closes every gap · 7-day free trial
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${sealed ? styles.ctaSealed : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {sealed ? "Sealing your vault…" : "Make it ironclad"}
            </span>
            {!sealed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="19"
                height="19"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your hidden photos are already private on this device. Premium adds
            extra layers of protection — it never changes what you have already
            secured, and your library is always yours to keep.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default IroncladPaywall
